import React, { useEffect, useState } from "react";
import { imgBaseUrl } from "../../api/api";
import "./StudentStudyCertificate1.css";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const SHORT_MONTHS = MONTHS.map((m) => m.slice(0, 3));

const ONES = [
  "",
  "First",
  "Second",
  "Third",
  "Fourth",
  "Fifth",
  "Sixth",
  "Seventh",
  "Eighth",
  "Ninth",
  "Tenth",
  "Eleventh",
  "Twelfth",
  "Thirteenth",
  "Fourteenth",
  "Fifteenth",
  "Sixteenth",
  "Seventeenth",
  "Eighteenth",
  "Nineteenth",
  "Twentieth",
  "Twenty First",
  "Twenty Second",
  "Twenty Third",
  "Twenty Fourth",
  "Twenty Fifth",
  "Twenty Sixth",
  "Twenty Seventh",
  "Twenty Eighth",
  "Twenty Ninth",
  "Thirtieth",
  "Thirty First",
];
const CARD = [
  "Zero",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
  "Thirteen",
  "Fourteen",
  "Fifteen",
  "Sixteen",
  "Seventeen",
  "Eighteen",
  "Nineteen",
];
const TENS = [
  "",
  "",
  "Twenty",
  "Thirty",
  "Forty",
  "Fifty",
  "Sixty",
  "Seventy",
  "Eighty",
  "Ninety",
];

const below100 = (n) =>
  n < 20
    ? CARD[n]
    : TENS[Math.floor(n / 10)] + (n % 10 ? " " + CARD[n % 10] : "");

const yearInWords = (y) => {
  const th = Math.floor(y / 1000);
  const rest = y % 1000;
  const h = Math.floor(rest / 100);
  const t = rest % 100;
  let s = `${CARD[th]} Thousand`;
  if (h) s += ` ${CARD[h]} Hundred`;
  if (t) s += ` ${below100(t)}`;
  return s;
};

const parseDate = (value) => {
  if (!value) return null;
  const d = new Date(String(value).slice(0, 10) + "T00:00:00");
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (value) => {
  const d = parseDate(value);
  if (!d) return "—";
  return `${String(d.getDate()).padStart(2, "0")}-${SHORT_MONTHS[d.getMonth()]}-${d.getFullYear()}`;
};

const dateInWords = (value) => {
  const d = parseDate(value);
  if (!d) return "—";
  return `${ONES[d.getDate()]} ${MONTHS[d.getMonth()]} ${yearInWords(d.getFullYear())}`;
};

const joinUrl = (base = "", path = "") => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${String(base).replace(/\/+$/, "")}/${String(path).replace(/^\/+/, "")}`;
};

export default function StudentStudyCertificate1({
  data,
  batch,
  issueDate,
  certificateNo,
}) {
  const [setting, setSetting] = useState({});

  useEffect(() => {
    try {
      setSetting(JSON.parse(localStorage.getItem("setting") ?? "{}"));
    } catch {
      setSetting({});
    }
  }, []);

  const student = data?.student_info ?? {};
  const isFemale = String(student?.gender ?? "").toLowerCase() === "female";
  const pronoun = isFemale ? "She" : "He";
  const relation = isFemale ? "Daughter" : "Son";
  const honorific = isFemale ? "Ms." : "Mr.";

  const logoUrl = joinUrl(imgBaseUrl, setting?.logo_img);
  const photoUrl = joinUrl(imgBaseUrl, student?.profile_picture);
  const signatureUrl = joinUrl(
    imgBaseUrl,
    setting?.signature_img ??
      setting?.principal_signature ??
      setting?.signature ??
      setting?.sign_img,
  );
  const session = batch?.sessions?.session ?? "—";
  const issued = issueDate
    ? formatDate(issueDate)
    : formatDate(new Date().toISOString());

  return (
    <div className="sc-page">
      <div className="sc-outer">
        {logoUrl && (
          <img
            className="sc-watermark"
            src={logoUrl}
            alt=""
            aria-hidden="true"
          />
        )}
        <div className="sc-inner">
          <div className="sc-header">
            <div className="sc-logo-box">
              {logoUrl && <img src={logoUrl} alt="logo" />}
            </div>
            <div className="sc-header-text">
              <h1 className="sc-inst-name">{setting?.name}</h1>
              <p className="sc-inst-meta">
                {[setting?.village, setting?.district, setting?.state]
                  .filter(Boolean)
                  .join(", ")}
                {setting?.pin_code ? ` - ${setting.pin_code}` : ""}
              </p>
              <p className="sc-inst-meta">
                {setting?.mobile && <>Mobile: {setting.mobile}</>}
                {setting?.secondary_mobile && `, ${setting.secondary_mobile}`}
                {setting?.email && <> • {setting.email}</>}
              </p>
            </div>
            <div className="sc-header-spacer" />
          </div>

          <div className="sc-title-block">
            <h2 className="sc-title">Study Certificate</h2>
            <div className="sc-title-rule">
              <span className="line" />
              <span className="dot" />
              <span className="line" />
            </div>
          </div>

          <div className="sc-meta-row">
            <div>
              Certificate No: <span>{certificateNo || "—"}</span>
            </div>
            <div>
              Date: <span>{issued}</span>
            </div>
          </div>

          <div className="sc-photo-row">
            <div className="sc-photo">
              {photoUrl ? (
                <img src={photoUrl} alt={student?.name} />
              ) : (
                <span>Photo</span>
              )}
            </div>
          </div>

          <div className="sc-body">
            <p>
              This is to certify that {honorific} <b>{student?.name}</b>{" "}
              {relation} of Mr. <b>{student?.father_name}</b> and Mrs.{" "}
              <b>{student?.mother_name}</b>, bearing Registration No.{" "}
              <b>{student?.registration_no}</b>, whose Date of Birth is (in
              figures) <b>{formatDate(student?.dob)}</b> (in words){" "}
              <b>{dateInWords(student?.dob)}</b>, is a bonafide student of{" "}
              <b>{setting?.name}</b>.
            </p>
            <p>
              {pronoun} is studying in <b>{batch?.course?.title ?? "—"}</b>
              {batch?.title ? (
                <>
                  {" "}
                  (Batch: <b>{batch.title}</b>)
                </>
              ) : null}{" "}
              for the session <b>{session}</b>.
            </p>
            <p>We wish {isFemale ? "her" : "him"} a bright future ahead.</p>
          </div>

          <div className="sc-footer">
            <div className="sc-footer-col">Place: {setting?.village}</div>
            <div className="sc-footer-col">
              {signatureUrl && (
                <img
                  className="sc-signature"
                  src={signatureUrl}
                  alt="Principal signature"
                />
              )}
              <div className="sc-sign-line">Principal Seal &amp; Signature</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
